import { bbox, intersect, area, distance, featureCollection } from '@turf/turf';
import { cleanAndValidateGeometry } from '../topology/topologyEngine';

/**
 * Детектор дубликатов объектов для SPD+
 */

/**
 * Извлечение кадастрового номера из объекта
 */
export function extractCadastralNumber(feature) {
    if (!feature || !feature.properties) return null;
    const props = feature.properties;
    const raw = props.cadastral_number || props.cadastralNumber || props.cadastral || props.attributes?.cadastral_number;
    if (!raw || typeof raw !== 'string') return null;
    return raw.trim().replace(/\s+/g, '');
}

/**
 * Извлечение названия объекта
 */
export function extractFeatureName(feature) {
    if (!feature || !feature.properties) return 'Без названия';
    const props = feature.properties;
    return props.name || props.title || props.id || 'Объект';
}

/**
 * Проверка пересечения BBox (быстрый фильтр)
 */
function safeIntersect(poly1, poly2) {
    try {
        const res = intersect(poly1, poly2);
        if (res !== undefined) return res;
    } catch (e) {}
    try {
        return intersect(featureCollection([poly1, poly2]));
    } catch (e) {}
    return null;
}

function doBBoxesIntersect(box1, box2) {
    return !(
        box1[2] < box2[0] ||
        box1[0] > box2[2] ||
        box1[3] < box2[1] ||
        box1[1] > box2[3]
    );
}

/**
 * Сравнение свойств двух объектов для формирования списка различий
 */
export function computeFeatureDiff(f1, f2) {
    const p1 = f1.properties || {};
    const p2 = f2.properties || {};

    const a1 = Math.round((area(f1) || 0) * 100) / 100;
    const a2 = Math.round((area(f2) || 0) * 100) / 100;

    const cad1 = extractCadastralNumber(f1);
    const cad2 = extractCadastralNumber(f2);

    return {
        name: {
            val1: extractFeatureName(f1),
            val2: extractFeatureName(f2),
            isDiff: extractFeatureName(f1) !== extractFeatureName(f2)
        },
        area: {
            val1: a1,
            val2: a2,
            delta: Math.round((a2 - a1) * 100) / 100,
            isDiff: Math.abs(a1 - a2) > 0.5
        },
        cadastral: {
            val1: cad1 || '—',
            val2: cad2 || '—',
            isDiff: cad1 !== cad2
        },
        category: {
            val1: p1.category || '—',
            val2: p2.category || '—',
            isDiff: (p1.category || '') !== (p2.category || '')
        },
        status: {
            val1: p1.status || '—',
            val2: p2.status || '—',
            isDiff: (p1.status || '') !== (p2.status || '')
        },
        type: {
            val1: f1.geometry?.type || '—',
            val2: f2.geometry?.type || '—',
            isDiff: f1.geometry?.type !== f2.geometry?.type
        }
    };
}

/**
/**
 * Сканирование базы объектов на наличие дублей и наложений
 * @param {Object} layersData - Объект с данными слоев { plot: FeatureCollection, building: FeatureCollection, ... }
 * @param {Object} options - Настройки поиска { minOverlapPercent: 5, checkCrossLayers: false }
 * @returns {Array<Object>} Список найденных пар дубликатов
 */
export function findDuplicates(layersData, options = {}) {
    const {
        minOverlapPercent = 5,
        checkCrossLayers = false // Сравниваем ТОЛЬКО объекты одного типа: участки с участками, здания со зданиями
    } = options;

    const allFeatures = [];
    const duplicates = [];
    const seenPairs = new Set();

    // Собираем все объекты с привязкой к типу
    Object.keys(layersData || {}).forEach(layerKey => {
        const collection = layersData[layerKey];
        if (collection && Array.isArray(collection.features)) {
            collection.features.forEach(feat => {
                if (feat && feat.geometry && feat.id) {
                    try {
                        const validated = cleanAndValidateGeometry(feat);
                        if (validated) {
                            const rawType = feat.properties?.type || (feat.geometry?.type === 'Point' ? 'marker' : layerKey);
                            const normType = (rawType === 'road' || rawType === 'line') ? 'line' : rawType;

                            allFeatures.push({
                                layer: layerKey,
                                normType,
                                feature: {
                                    ...validated,
                                    id: feat.id,
                                    properties: { ...(feat.properties || {}), layerKey, type: normType }
                                },
                                bbox: bbox(validated)
                            });
                        }
                    } catch (e) {
                        // ignore invalid geometry
                    }
                }
            });
        }
    });

    const total = allFeatures.length;

    for (let i = 0; i < total; i++) {
        for (let j = i + 1; j < total; j++) {
            const item1 = allFeatures[i];
            const item2 = allFeatures[j];

            // Важнейшее правило: сравниваем только объекты ОДНОГО ТИПА (участки-участки, здания-здания)
            if (!checkCrossLayers && item1.normType !== item2.normType) {
                continue;
            }

            const pairKey = [item1.feature.id, item2.feature.id].sort().join(':::');
            if (seenPairs.has(pairKey)) continue;

            const cad1 = extractCadastralNumber(item1.feature);
            const cad2 = extractCadastralNumber(item2.feature);

            let isDuplicate = false;
            let matchType = '';
            let matchCategory = 'overlap'; // 'exact' | 'containment' | 'overlap' | 'cadastral' | 'proximity'
            let overlapPercent = 0;
            let overlap1 = 0;
            let overlap2 = 0;
            let reason = '';
            let geomReason = '';

            // 1. Проверка совпадения кадастровых номеров
            if (cad1 && cad2 && cad1 === cad2) {
                isDuplicate = true;
                matchType = 'cadastral';
                matchCategory = 'cadastral';
                reason = `Одинаковый кадастровый номер: ${cad1}`;
            }

            // 2. Проверка геометрического пересечения для полигонов (участки, здания)
            const isPoly1 = item1.feature.geometry.type === 'Polygon' || item1.feature.geometry.type === 'MultiPolygon';
            const isPoly2 = item2.feature.geometry.type === 'Polygon' || item2.feature.geometry.type === 'MultiPolygon';

            if (isPoly1 && isPoly2 && doBBoxesIntersect(item1.bbox, item2.bbox)) {
                try {
                    const inter = safeIntersect(item1.feature, item2.feature);
                    if (inter) {
                        const areaInter = area(inter);
                        const area1 = area(item1.feature);
                        const area2 = area(item2.feature);

                        if (area1 > 0 && area2 > 0) {
                            overlap1 = (areaInter / area1) * 100; // % первого внутри второго
                            overlap2 = (areaInter / area2) * 100; // % второго внутри первого
                            const maxOverlap = Math.max(overlap1, overlap2);

                            if (maxOverlap >= minOverlapPercent) {
                                isDuplicate = true;
                                overlapPercent = Math.round(maxOverlap * 10) / 10;

                                if (overlap1 >= 97 && overlap2 >= 97) {
                                    // Полный дубликат 1 в 1
                                    matchCategory = 'exact';
                                    matchType = matchType ? `${matchType}+exact` : 'exact';
                                    geomReason = `Точная копия геометрии 1 в 1 (${overlapPercent}% совпадение)`;
                                } else if (overlap1 >= 95 || overlap2 >= 95) {
                                    // Один объект полностью находится внутри другого
                                    matchCategory = 'containment';
                                    matchType = matchType ? `${matchType}+containment` : 'containment';
                                    const isItem1Inside = overlap1 >= overlap2;
                                    const innerName = extractFeatureName(isItem1Inside ? item1.feature : item2.feature);
                                    const outerName = extractFeatureName(isItem1Inside ? item2.feature : item1.feature);
                                    const pct = Math.round(Math.max(overlap1, overlap2) * 10) / 10;
                                    geomReason = `Объект «${innerName}» находится внутри «${outerName}» (${pct}%)`;
                                } else {
                                    // Частичное наложение / пересечение контуров
                                    matchCategory = matchCategory === 'cadastral' ? 'cadastral' : 'overlap';
                                    matchType = matchType ? `${matchType}+overlap` : 'overlap';
                                    geomReason = `Пересечение контуров: ${overlapPercent}%`;
                                }

                                if (!reason) {
                                    reason = geomReason;
                                } else {
                                    reason = `${reason} • ${geomReason}`;
                                }
                            }
                        }
                    }
                } catch (e) {
                    // Turf intersect error handling
                }
            }

            // 3. Проверка близости точек (для точечных меток)
            const isPoint1 = item1.feature.geometry.type === 'Point';
            const isPoint2 = item2.feature.geometry.type === 'Point';

            if (isPoint1 && isPoint2) {
                const distM = distance(item1.feature, item2.feature, { units: 'kilometers' }) * 1000;
                if (distM < 0.5) {
                    isDuplicate = true;
                    matchCategory = 'exact';
                    matchType = matchType ? `${matchType}+exact` : 'exact';
                    const ptReason = `Точки совпадают 1 в 1 (${distM < 0.1 ? '0 м' : `${Math.round(distM * 10) / 10} м`})`;
                    reason = reason ? `${reason} • ${ptReason}` : ptReason;
                } else if (distM < 5.0) {
                    isDuplicate = true;
                    if (!matchCategory || matchCategory === 'overlap') matchCategory = 'proximity';
                    matchType = matchType ? `${matchType}+proximity` : 'proximity';
                    const ptReason = `Близкое наложение меток (${Math.round(distM * 10) / 10} м)`;
                    reason = reason ? `${reason} • ${ptReason}` : ptReason;
                }
            }

            if (isDuplicate) {
                seenPairs.add(pairKey);
                duplicates.push({
                    id: pairKey,
                    item1: {
                        layer: item1.layer,
                        normType: item1.normType,
                        id: item1.feature.id,
                        feature: item1.feature
                    },
                    item2: {
                        layer: item2.layer,
                        normType: item2.normType,
                        id: item2.feature.id,
                        feature: item2.feature
                    },
                    matchType,
                    matchCategory,
                    overlapPercent,
                    overlap1: Math.round(overlap1 * 10) / 10,
                    overlap2: Math.round(overlap2 * 10) / 10,
                    reason,
                    geomReason,
                    diffs: computeFeatureDiff(item1.feature, item2.feature)
                });
            }
        }
    }

    return duplicates;
}

/**
 * Палитра ярких контрастных цветов для выделения групп дубликатов на карте и в списке
 */
export const DUPLICATE_PALETTE = [
    '#ef4444', // Красный
    '#3b82f6', // Синий
    '#10b981', // Изумрудный
    '#f59e0b', // Янтарный
    '#8b5cf6', // Фиолетовый
    '#06b6d4', // Бирюзовый
    '#ec4899', // Розовый
    '#84cc16', // Лайм
    '#f97316', // Оранжевый
    '#6366f1'  // Индиго
];

/**
 * Поиск дубликатов напрямую из плоского массива features
 */
export function findDuplicatesFromFeatures(features, options = {}) {
    const layersData = {
        plot: { type: 'FeatureCollection', features: [] },
        building: { type: 'FeatureCollection', features: [] },
        road: { type: 'FeatureCollection', features: [] },
        marker: { type: 'FeatureCollection', features: [] }
    };
    (features || []).forEach(f => {
        if (!f || !f.geometry) return;
        const type = f.properties?.type || (f.geometry?.type === 'Point' ? 'marker' : 'plot');
        const key = type === 'line' ? 'road' : type;
        if (layersData[key]) {
            layersData[key].features.push(f);
        } else {
            layersData.plot.features.push(f);
        }
    });
    return findDuplicates(layersData, options);
}

/**
 * Описание категорий совпадения для UI
 */
export const CATEGORY_INFO = {
    exact: {
        key: 'exact',
        label: '1 в 1 (Точная копия)',
        shortLabel: '1 в 1',
        icon: '🔴',
        color: '#ef4444',
        badgeBg: '#fee2e2',
        badgeColor: '#ef4444'
    },
    containment: {
        key: 'containment',
        label: 'Объект внутри другого',
        shortLabel: 'Вложенность',
        icon: '🟠',
        color: '#f97316',
        badgeBg: '#ffedd5',
        badgeColor: '#ea580c'
    },
    overlap: {
        key: 'overlap',
        label: 'Пересечение контуров',
        shortLabel: 'Наложение',
        icon: '🟡',
        color: '#eab308',
        badgeBg: '#fef9c3',
        badgeColor: '#ca8a04'
    },
    cadastral: {
        key: 'cadastral',
        label: 'Совпадение кад. номера',
        shortLabel: 'Кадастровый',
        icon: '🔵',
        color: '#3b82f6',
        badgeBg: '#dbeafe',
        badgeColor: '#2563eb'
    },
    proximity: {
        key: 'proximity',
        label: 'Совпадение точек',
        shortLabel: 'Координаты',
        icon: '🟣',
        color: '#8b5cf6',
        badgeBg: '#ede9fe',
        badgeColor: '#7c3aed'
    }
};

/**
 * Объединение найденных пар дубликатов в связные группы (кластеры)
 * @param {Array<Object>} duplicatesList - Список пар дубликатов
 * @returns {Array<Object>} Список сгруппированных дубликатов
 */
export function groupDuplicates(duplicatesList) {
    if (!Array.isArray(duplicatesList) || duplicatesList.length === 0) {
        return [];
    }

    const adj = new Map();
    const featureMap = new Map();
    const pairMap = new Map();

    duplicatesList.forEach(dup => {
        const id1 = dup.item1.id;
        const id2 = dup.item2.id;
        if (!adj.has(id1)) adj.set(id1, new Set());
        if (!adj.has(id2)) adj.set(id2, new Set());
        adj.get(id1).add(id2);
        adj.get(id2).add(id1);

        featureMap.set(id1, dup.item1.feature);
        featureMap.set(id2, dup.item2.feature);

        const pairKey = [id1, id2].sort().join(':::');
        pairMap.set(pairKey, dup);
    });

    const visited = new Set();
    const groups = [];

    let groupIndex = 0;
    for (const startId of adj.keys()) {
        if (visited.has(startId)) continue;

        const queue = [startId];
        visited.add(startId);
        const groupFeatures = [];
        const groupPairs = [];
        const groupCategories = new Set();
        const groupTypes = new Set();
        const groupReasons = new Set();

        while (queue.length > 0) {
            const currentId = queue.shift();
            groupFeatures.push(featureMap.get(currentId));

            const neighbors = adj.get(currentId) || [];
            for (const neighborId of neighbors) {
                const pairKey = [currentId, neighborId].sort().join(':::');
                const pair = pairMap.get(pairKey);
                if (pair) {
                    if (!groupPairs.some(p => p.id === pair.id)) {
                        groupPairs.push(pair);
                    }
                    if (pair.matchCategory) {
                        groupCategories.add(pair.matchCategory);
                    }
                    if (pair.matchType) {
                        pair.matchType.split('+').forEach(t => groupTypes.add(t));
                    }
                    if (pair.reason) {
                        groupReasons.add(pair.reason);
                    }
                }

                if (!visited.has(neighborId)) {
                    visited.add(neighborId);
                    queue.push(neighborId);
                }
            }
        }

        const color = DUPLICATE_PALETTE[groupIndex % DUPLICATE_PALETTE.length];
        const typesList = Array.from(groupTypes);
        const categoriesList = Array.from(groupCategories);

        // Определяем преобладающую категорию по приоритету (exact > containment > overlap > cadastral > proximity)
        let primaryCategory = 'overlap';
        if (categoriesList.includes('exact')) primaryCategory = 'exact';
        else if (categoriesList.includes('containment')) primaryCategory = 'containment';
        else if (categoriesList.includes('overlap')) primaryCategory = 'overlap';
        else if (categoriesList.includes('cadastral')) primaryCategory = 'cadastral';
        else if (categoriesList.includes('proximity')) primaryCategory = 'proximity';

        const categoryInfo = CATEGORY_INFO[primaryCategory] || CATEGORY_INFO.overlap;

        groups.push({
            id: `dup-group-${groupIndex + 1}`,
            index: groupIndex + 1,
            color,
            features: groupFeatures,
            primaryCategory,
            categoryInfo,
            categories: categoriesList,
            types: typesList,
            reasons: Array.from(groupReasons),
            pairs: groupPairs
        });

        groupIndex++;
    }

    return groups;
}

/**
 * Получение детальной информации о дублировании конкретного объекта
 */
export function getFeatureDuplicateInfo(featureId, duplicateGroups) {
    if (!featureId || !Array.isArray(duplicateGroups)) {
        return null;
    }

    for (const group of duplicateGroups) {
        const foundFeature = group.features.find(f => f.id === featureId);
        if (foundFeature) {
            const otherFeatures = group.features.filter(f => f.id !== featureId);
            const otherNames = otherFeatures.map(f => `«${extractFeatureName(f)}»`).join(', ');

            const relatedPairs = group.pairs.filter(p => p.item1.id === featureId || p.item2.id === featureId);

            const hasExact = group.categories.includes('exact') || relatedPairs.some(p => p.matchCategory === 'exact');
            const hasContainment = group.categories.includes('containment') || relatedPairs.some(p => p.matchCategory === 'containment');
            const hasOverlap = group.categories.includes('overlap') || relatedPairs.some(p => p.matchCategory === 'overlap');
            const hasCadastral = group.categories.includes('cadastral') || relatedPairs.some(p => p.matchType?.includes('cadastral'));
            const hasProximity = group.categories.includes('proximity') || relatedPairs.some(p => p.matchType?.includes('proximity'));

            const exactPair = relatedPairs.find(p => p.matchCategory === 'exact');
            const containmentPair = relatedPairs.find(p => p.matchCategory === 'containment');
            const overlapPair = relatedPairs.find(p => p.matchCategory === 'overlap');
            const cadPair = relatedPairs.find(p => p.matchType?.includes('cadastral'));
            const proxPair = relatedPairs.find(p => p.matchType?.includes('proximity'));

            let primaryCategory = group.primaryCategory;
            if (exactPair) primaryCategory = 'exact';
            else if (containmentPair) primaryCategory = 'containment';
            else if (overlapPair) primaryCategory = 'overlap';
            else if (cadPair) primaryCategory = 'cadastral';
            else if (proxPair) primaryCategory = 'proximity';

            const categoryInfo = CATEGORY_INFO[primaryCategory] || CATEGORY_INFO.overlap;

            let geomReason = '';
            if (exactPair) {
                const other = exactPair.item1.id === featureId ? exactPair.item2.feature : exactPair.item1.feature;
                geomReason = `Точная копия 1 в 1 с объектом «${extractFeatureName(other)}» (${exactPair.overlapPercent}% совпадение геометрии)`;
            } else if (containmentPair) {
                geomReason = containmentPair.geomReason || containmentPair.reason;
            } else if (overlapPair) {
                const other = overlapPair.item1.id === featureId ? overlapPair.item2.feature : overlapPair.item1.feature;
                geomReason = `Наложение контуров ${overlapPair.overlapPercent}% с объектом «${extractFeatureName(other)}»`;
            } else if (proxPair) {
                const other = proxPair.item1.id === featureId ? proxPair.item2.feature : proxPair.item1.feature;
                geomReason = `${proxPair.reason} с объектом «${extractFeatureName(other)}»`;
            }

            const cadReason = cadPair
                ? `${cadPair.reason} (совпадает с «${extractFeatureName(cadPair.item1.id === featureId ? cadPair.item2.feature : cadPair.item1.feature)}»)`
                : (hasCadastral ? `Кадастровый номер совпадает с: ${otherNames}` : '');

            return {
                isDuplicate: true,
                group,
                groupColor: group.color,
                primaryCategory,
                categoryInfo,
                otherFeatures,
                hasExactMatch: hasExact,
                hasContainmentMatch: hasContainment,
                hasGeometryOverlap: hasOverlap,
                hasGeometryDuplicate: hasExact || hasContainment || hasOverlap,
                geometryReason: geomReason,
                hasCadastralDuplicate: hasCadastral,
                cadastralReason: cadReason,
                hasProximityDuplicate: hasProximity,
                proximityReason: proxPair?.reason || '',
                reasons: group.reasons
            };
        }
    }

    return null;
}


/**
 * Слияние свойств двух объектов при объединении дубликатов
 * @param {Object} masterFeature - Основной сохраняемый объект
 * @param {Object} duplicateFeature - Поглощаемый дубликат
 * @returns {Object} Объединенный объект
 */
export function mergeDuplicateAttributes(masterFeature, duplicateFeature) {
    const pMaster = { ...(masterFeature.properties || {}) };
    const pDup = { ...(duplicateFeature.properties || {}) };

    // Заполняем пустые или отсутствующие поля мастера данными из дубликата
    Object.keys(pDup).forEach(key => {
        if (key === 'id' || key === 'layerKey') return;
        if (pMaster[key] === undefined || pMaster[key] === null || pMaster[key] === '' || pMaster[key] === '—') {
            pMaster[key] = pDup[key];
        }
    });

    return {
        ...masterFeature,
        properties: {
            ...pMaster,
            merged_from: duplicateFeature.id,
            merged_at: new Date().toISOString()
        }
    };
}

